import { keepPreviousData, useQuery } from "@tanstack/react-query";

import relationService from "@services/relation.service";
import { DEBOUNCE_MS } from "@config/constants";
import { TagsTypes } from "@components/Sections/TagsSection/constants";
import { QUERY_CACHE_SHORT_EXPIRATION_MS, TAGGING_OPTIONS_QUERY_KEY } from "@config/query";
import { IMeetingTag, OptionsForTags, Tag } from "@interfaces/tag.interface";

const EXTRA_ITEMS_FOR_CACHE = 1;

export const DEFAULT_LIMIT_TAGS = 2;
export const MAX_LIMIT_TAGS = 15;

interface ISearchFn {
  query: string;
  limit: number | null;
  contentType: string;
  keyForTypes: keyof typeof TagsTypes;
  excludedIds?: string[];
  /** Overrides the `keyForTypes` set. */
  tagTypes?: string[];
  signal: AbortSignal;
}

export const searchFn = async ({
  query,
  limit,
  contentType,
  keyForTypes,
  excludedIds,
  tagTypes,
  signal,
}: ISearchFn) => {
  return new Promise<OptionsForTags>((resolve, reject) => {
    const timeout = setTimeout(() => {
      const params = new URLSearchParams();

      params.append("content_type", contentType);
      params.append("types", (tagTypes ?? TagsTypes[keyForTypes]).toString());

      if (excludedIds && excludedIds.length > 0) {
        params.append("exclude", excludedIds.toString());
      }

      if (query.length === 0) {
        if (limit) params.append("limit", String(limit));
      } else {
        params.append("query", query);
        params.append("limit", String(16));
      }

      relationService
        .getTaggingOptions("?" + params.toString())
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    }, DEBOUNCE_MS);

    signal.addEventListener("abort", () => {
      clearTimeout(timeout);
      reject(new Error("Aborted"));
    });
  });
};

export function useSearchTags(
  query: string,
  limit: number | null,
  contentType: string,
  keyForTypes: keyof typeof TagsTypes,
  excludedIds?: string[],
  tagTypes?: string[]
) {
  const updatedLimit = limit || MAX_LIMIT_TAGS;

  const keepTypingData = {
    deals: false,
    companies: false,
    people: false,
    meetings: false,
    ai_notes: false,
    users: false,
    documents: false,
    themes: false,
    threads: false,
    folders: false,
    funds: false,
    opportunities: false,
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      TAGGING_OPTIONS_QUERY_KEY,
      query,
      limit,
      contentType,
      keyForTypes,
      excludedIds,
      tagTypes,
    ],
    queryFn: ({ signal }) => {
      const limitForSearch = updatedLimit + EXTRA_ITEMS_FOR_CACHE;
      return searchFn({
        query,
        limit: limitForSearch,
        contentType,
        keyForTypes,
        excludedIds,
        tagTypes,
        signal,
      });
    },
    select: (data) => {
      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
          if (!Array.isArray(value)) return [key, []];

          const filteredValue = (value as Tag[]).filter((item) => !excludedIds?.includes(item.id));

          if (key === "meetings") {
            const now = new Date().toISOString();
            const pastMeetings = filteredValue
              .filter((item) => (item as IMeetingTag).start_time < now)
              .sort((a, b) =>
                (b as IMeetingTag).start_time > (a as IMeetingTag).start_time ? 1 : -1
              );
            const futureMeetings = filteredValue
              .filter((item) => (item as IMeetingTag).start_time >= now)
              .sort((a, b) =>
                (a as IMeetingTag).start_time > (b as IMeetingTag).start_time ? 1 : -1
              );

            if (
              (pastMeetings.length > updatedLimit || futureMeetings.length > updatedLimit) &&
              query.length > 0
            ) {
              keepTypingData[key] = true;
            }

            let pastCount = 0;
            let futureCount = 0;

            // Calculate how many items to take from each array
            if (pastMeetings.length === 0) {
              // Case 4: No past meetings, take all from future
              futureCount = Math.min(updatedLimit, futureMeetings.length);
            } else if (futureMeetings.length === 0) {
              // Case 5: No future meetings, take all from past
              pastCount = Math.min(updatedLimit, pastMeetings.length);
            } else {
              // Cases 1, 2, 3: Both arrays have items
              const totalAvailable = pastMeetings.length + futureMeetings.length;

              if (totalAvailable <= updatedLimit) {
                // If total available is less than or equal to limit, take all
                pastCount = pastMeetings.length;
                futureCount = futureMeetings.length;
              } else {
                // If we have more than limit, prioritize based on availability
                if (pastMeetings.length <= Math.round((updatedLimit * 2) / 3)) {
                  // Case 2: Few past meetings, prioritize future
                  pastCount = pastMeetings.length;
                  futureCount = updatedLimit - pastCount;
                } else if (futureMeetings.length <= Math.round((updatedLimit * 1) / 3)) {
                  // Case 3: Few future meetings, prioritize past
                  futureCount = futureMeetings.length;
                  pastCount = updatedLimit - futureCount;
                } else {
                  // Case 1: Both have sufficient meetings, use balanced approach
                  pastCount = Math.round((updatedLimit * 2) / 3);
                  futureCount = updatedLimit - pastCount;
                }
              }
            }

            const formattedData = [
              ...pastMeetings.slice(0, pastCount).reverse(),
              ...futureMeetings.slice(0, futureCount),
            ];

            return [key, formattedData];
          }

          const formattedData = filteredValue.slice(0, updatedLimit);
          if (filteredValue.length > updatedLimit && query.length > 0) {
            keepTypingData[key as keyof typeof keepTypingData] = true;
          }
          return [key, formattedData];
        })
      ) as OptionsForTags;
    },
    staleTime: QUERY_CACHE_SHORT_EXPIRATION_MS,
    retry: false,
    placeholderData: keepPreviousData,
  });

  return {
    data,
    isLoading,
    keepTypingData,
  };
}
